import { ReactElement } from 'react';

import { styled } from '@mui/material';

import {
  Icons,
  RatioProgressBar,
  Tooltip,
  TooltipProps,
  ExternalLink,
  Typography,
  Stack,
} from '@cast/design-system';
import { formatPercentage, getDocsLink } from '@cast/utils';

import { WorkloadRecommendation } from 'features/workload-optimization';
import { WORKLOAD_RECOMMENDATION_MIN_CONFIDENCE } from 'features/workload-optimization/constants';

import { ThresholdReachedAtLabel } from './ThresholdReachedAtLabel';

const Icon = styled(Icons.WarningCircle)(({ theme }) => ({
  color: theme.palette.yellow[500],
  fontSize: 16,
}));

type Props = {
  content: ReactElement | string;
  tooltipPlacement?: TooltipProps['placement'];
  recommendation: WorkloadRecommendation;
  children: ReactElement;
};

export const LowConfidenceTooltip = ({
  content,
  tooltipPlacement = 'bottom',
  recommendation,
  children,
}: Props) => {
  return (
    <Tooltip
      title={
        <Stack
          gap={8}
          data-testid="low-confidence-tooltip"
          onClick={(event) => {
            // prevent parent element action when clicking a link inside this tooltip
            event.stopPropagation();
          }}
        >
          <Stack gap={8} direction="row" alignItems="center">
            <Icon weight="fill" fontSize={20} />
            <Typography variant="P14B">Low data confidence</Typography>
          </Stack>

          <Stack pl={24} gap={16}>
            <Stack gap={4}>
              <Stack direction="row" justifyContent="space-between">
                <Typography
                  variant="L10M"
                  color="yellow.500"
                  data-testid="confidence"
                >
                  {formatPercentage(recommendation.confidence * 100, {
                    roundBy: 0,
                  })}
                </Typography>
                {recommendation.estimatedThresholdReachedAt && (
                  <ThresholdReachedAtLabel
                    reachedAt={recommendation.estimatedThresholdReachedAt}
                  />
                )}
              </Stack>
              <RatioProgressBar
                hideBorders
                size="sm"
                total={1}
                bars={[
                  {
                    value: recommendation.confidence,
                    color: 'yellow.500',
                  },
                  {
                    value: recommendation.estimatedThresholdReachedAt
                      ? WORKLOAD_RECOMMENDATION_MIN_CONFIDENCE -
                        recommendation.confidence
                      : 0,
                    color: '#F5B30759',
                  },
                  {
                    value: 1,
                    color: 'grey.600',
                  },
                ]}
              />
            </Stack>

            <Stack gap={8}>{content}</Stack>

            <span>
              <ExternalLink
                link={getDocsLink('docs_woop_confidence')}
                sx={{ color: 'blue.400' }}
              >
                How we calculate this
              </ExternalLink>
            </span>
          </Stack>
        </Stack>
      }
      placement={tooltipPlacement}
      sx={{
        width: 340,
        maxWidth: 340,
        minWidth: 340,
      }}
      arrow
    >
      {children}
    </Tooltip>
  );
};
